import * as React from "react";
import {
  ChevronUp,
  Home,
  Notebook,
  User2,
  Star,
  Inbox,
  Calendar,
  LogOut,
  Network,
  ListChecks,
} from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { getLoginUrl, logout as apiLogout } from "@/lib/api/auth";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { TextLabel } from "@/components/ui/text-label";
import { IconContainer } from "@/components/ui/icon-container";

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user?: any;
  notes?: Array<Record<string, unknown>>;
}

export function AppSidebar({
  user: serverUser,
  notes: _serverNotes = [],
  ...props
}: AppSidebarProps) {
  const location = useLocation();
  const { user: clientUser, isAuthenticated } = useAuth();

  const user = serverUser || clientUser;
  const userIsAuthenticated = !!(serverUser || isAuthenticated);

  const isActiveRoute = (path: string) => {
    if (path === "/" && location.pathname === "/") return true;
    return path !== "/" && location.pathname.startsWith(path);
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" className="font-semibold tracking-tight">
                <IconContainer>
                  <Notebook className="size-4" />
                </IconContainer>
                <TextLabel title="Notebook" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Overview</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActiveRoute("/") && location.pathname === "/"}
                  tooltip="Home"
                >
                  <Link to="/">
                    <Home />
                    <span>Home</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {userIsAuthenticated && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActiveRoute("/inbox")}
                      tooltip="Inbox"
                    >
                      <Link to="/inbox">
                        <Inbox />
                        <span>Inbox</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActiveRoute("/today")}
                      tooltip="Today"
                    >
                      <Link to="/today">
                        <Star />
                        <span>Today</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActiveRoute("/upcoming")}
                      tooltip="Upcoming"
                    >
                      <Link to="/upcoming">
                        <Calendar />
                        <span>Upcoming</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActiveRoute("/lists")}
                      tooltip="Lists"
                    >
                      <Link to="/lists">
                        <ListChecks />
                        <span>Lists</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {userIsAuthenticated && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActiveRoute("/entities")}
                tooltip="Entities"
              >
                <Link to="/entities">
                  <Network className="size-4" />
                  <span>Entities</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            {userIsAuthenticated && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.name || "User"}
                        className="size-4 rounded-full"
                      />
                    ) : (
                      <User2 className="size-4" />
                    )}
                    <TextLabel
                      title={user.name || "User"}
                      subtitle={user.email}
                    />
                    <ChevronUp className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg bg-sidebar-accent"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <ThemeToggle variant="dropdown" />
                  <DropdownMenuItem onClick={async () => await apiLogout()}>
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarMenuButton
                size="lg"
                onClick={() => {
                  const loginUrl = getLoginUrl(location.pathname);
                  window.location.href = loginUrl;
                }}
                className="cursor-pointer"
              >
                <User2 className="size-4" />
                <TextLabel title="Sign in" subtitle="Sign in with Google" />
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
